import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LookupTable, LookupTableData } from '../types/model.types';
import { LookupTableEditor } from './lookup-table-editor';
import { useModelStore } from '../store/model-store';
import { useToast } from '@/lib/hooks/use-toast';
import { Plus, Edit, Trash2, Table2 } from 'lucide-react';

interface LookupTableManagerProps {
  modelId: string;
  lookupTables: LookupTable[];
}

export function LookupTableManager({ modelId, lookupTables }: LookupTableManagerProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<LookupTable | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    code: string;
    rowDimension: string;
    colDimension: string;
    data: LookupTableData;
  }>({
    name: '',
    code: '',
    rowDimension: '',
    colDimension: '',
    data: { rows: [], cols: [], values: [] },
  });

  const [deleteTableId, setDeleteTableId] = useState<string | null>(null);

  const { createLookupTable, updateLookupTable, deleteLookupTable, isLoading } = useModelStore();
  const { toast } = useToast();

  const handleCreate = () => {
    setEditingTable(null);
    setFormData({
      name: '',
      code: '',
      rowDimension: '',
      colDimension: '',
      data: { rows: [], cols: [], values: [] },
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (table: LookupTable) => {
    setEditingTable(table);
    setFormData({
      name: table.name,
      code: table.code ?? '',
      rowDimension: table.rowDimension,
      colDimension: table.colDimension,
      data: table.data,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (tableId: string) => {
    setDeleteTableId(tableId);
  };

  const confirmDeleteTable = async () => {
    if (!deleteTableId) return;
    try {
      await deleteLookupTable(modelId, deleteTableId);
      toast({ title: 'Tabla eliminada', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteTableId(null);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code || !formData.rowDimension || !formData.colDimension) {
      toast({ title: 'Completa todos los campos', variant: 'destructive' });
      return;
    }

    try {
      if (editingTable) {
        await updateLookupTable(modelId, editingTable.id, formData);
        toast({ title: 'Tabla actualizada', variant: 'success' });
      } else {
        await createLookupTable(modelId, formData);
        toast({ title: 'Tabla creada', variant: 'success' });
      }
      setIsDialogOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Table2 className="w-5 h-5" />
                Tablas de Lookup
              </CardTitle>
              <CardDescription>Matrices de precios basadas en 2 dimensiones</CardDescription>
            </div>
            <Button onClick={handleCreate} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tabla
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lookupTables.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground text-sm">No hay tablas de lookup configuradas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lookupTables.map((table) => (
                <div
                  key={table.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{table.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {table.code} — {table.rowDimension} × {table.colDimension} (
                      {table.data.rows.length}×{table.data.cols.length})
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(table)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(table.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTable ? 'Editar Tabla de Lookup' : 'Nueva Tabla de Lookup'}
            </DialogTitle>
            <DialogDescription>
              Define una matriz de precios basada en dos dimensiones
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Precios por País y Producto"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label>Código</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="PRECIO_PAIS_PRODUCTO"
                  className="uppercase font-mono"
                  disabled={isLoading || !!editingTable}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dimensión de Filas</Label>
                <Input
                  value={formData.rowDimension}
                  onChange={(e) => setFormData({ ...formData, rowDimension: e.target.value })}
                  placeholder="País"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label>Dimensión de Columnas</Label>
                <Input
                  value={formData.colDimension}
                  onChange={(e) => setFormData({ ...formData, colDimension: e.target.value })}
                  placeholder="Nivel de Producto"
                  disabled={isLoading}
                />
              </div>
            </div>
            <LookupTableEditor
              data={formData.data}
              rowDimension={formData.rowDimension || 'Fila'}
              colDimension={formData.colDimension || 'Columna'}
              onChange={(data) => setFormData({ ...formData, data })}
              disabled={isLoading}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isLoading}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isLoading}>
                {editingTable ? 'Actualizar' : 'Crear Tabla'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTableId} onOpenChange={(open) => !open && setDeleteTableId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará esta tabla de lookup de forma permanente. Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteTable}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default LookupTableManager;
